// S3 (v0.14.0) — Entity bulk CSV import.
//
// Flow:
//   1. Download template (GET /entities/bulk-import/template)
//   2. Drop / pick a .csv file; preview first 10 rows via papaparse
//   3. Click "Validate and Import" → POST /entities/bulk-import
//   4. Render row-level errors on 400, success count on 200

import { useRef, useState } from "react";
import Papa from "papaparse";
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  DownloadIcon,
  FileUpIcon,
  UploadIcon,
} from "lucide-react";

import {
  useBulkImportEntities,
  type BulkImportRowError,
} from "@/api/entities";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";


const EXPECTED_HEADERS = [
  "name",
  "code",
  "parent_code",
  "entity_type_name",
  "gps_lat",
  "gps_lng",
  "address",
] as const;


export default function BulkImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [rowPreview, setRowPreview] = useState<string[][] | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<BulkImportRowError[]>([]);
  const [success, setSuccess] = useState<{
    imported_count: number;
    entities: { id: string; name: string; code: string }[];
  } | null>(null);
  const importMut = useBulkImportEntities();
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  }

  function handleFile(f: File) {
    setFile(f);
    setParseError(null);
    setServerErrors([]);
    setSuccess(null);
    Papa.parse<string[]>(f, {
      skipEmptyLines: true,
      complete: (results) => {
        const allRows = results.data;
        if (allRows.length === 0) {
          setParseError("File is empty");
          setRowPreview(null);
          setRowCount(0);
          return;
        }
        const header = allRows[0].map((s) => s.trim().toLowerCase());
        if (
          header.length !== EXPECTED_HEADERS.length ||
          !header.every((h, i) => h === EXPECTED_HEADERS[i])
        ) {
          setParseError(
            `Header row must be: ${EXPECTED_HEADERS.join(",")} — got: ${header.join(",")}`,
          );
          setRowPreview(null);
          setRowCount(0);
          return;
        }
        const data = allRows.slice(1);
        setRowCount(data.length);
        setRowPreview([allRows[0], ...data.slice(0, 10)]);
      },
      error: (err) => setParseError(err.message),
    });
  }

  async function handleDownloadTemplate() {
    const res = await api.get("/api/v1/entities/bulk-import/template", {
      responseType: "blob",
    });
    const blob = res.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "entity_import_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async function handleImport() {
    if (!file) return;
    setServerErrors([]);
    setSuccess(null);
    try {
      const result = await importMut.mutateAsync(file);
      setSuccess({
        imported_count: result.imported_count,
        entities: result.entities,
      });
      setFile(null);
      setRowPreview(null);
      setRowCount(0);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        .response?.data?.detail;
      if (
        detail &&
        typeof detail === "object" &&
        "errors" in (detail as Record<string, unknown>)
      ) {
        setServerErrors(
          (detail as { errors: BulkImportRowError[] }).errors ?? [],
        );
      } else if (typeof detail === "string") {
        setServerErrors([{ row: 0, error: detail }]);
      } else {
        setServerErrors([{ row: 0, error: "Import failed" }]);
      }
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Bulk Import Entities
        </h1>
        <p className="text-sm text-muted-foreground">
          Upload a CSV to create many entities at once. The file must
          match the template format exactly.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Step 1 — Download template</p>
              <p className="text-xs text-muted-foreground">
                CSV with the canonical headers + three example rows.
              </p>
            </div>
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <DownloadIcon className="mr-1 h-3.5 w-3.5" />
              Download CSV Template
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-medium">Step 2 — Upload CSV</p>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "cursor-pointer rounded border-2 border-dashed border-border bg-muted/20 p-6 text-center text-sm transition-colors",
              file && "border-emerald-400 bg-emerald-50/40",
            )}
          >
            <FileUpIcon className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            {file ? (
              <>
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {rowCount} row{rowCount === 1 ? "" : "s"} parsed
                </p>
              </>
            ) : (
              <>
                <p>Drop a .csv file here, or click to browse.</p>
                <p className="text-xs text-muted-foreground">
                  Header row must match the template.
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>
          {parseError && (
            <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600">
              <AlertCircleIcon className="mr-1 inline h-3 w-3" />
              {parseError}
            </p>
          )}
        </CardContent>
      </Card>

      {rowPreview && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <p className="text-sm font-medium">
              Step 3 — Preview (first 10 rows)
            </p>
            <div className="max-h-72 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    {rowPreview[0].map((h, i) => (
                      <th key={i} className="px-2 py-1 text-left font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rowPreview.slice(1).map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {row.map((cell, j) => (
                        <td key={j} className="px-2 py-1 font-mono">
                          {cell || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={handleImport}
              disabled={importMut.isPending}
              className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
            >
              <UploadIcon className="mr-1 h-3.5 w-3.5" />
              {importMut.isPending
                ? "Importing…"
                : `Validate and Import (${rowCount})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {serverErrors.length > 0 && (
        <Card className="border-red-500/30">
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-red-700">
              <AlertCircleIcon className="mr-1 inline h-3.5 w-3.5" />
              Import failed — {serverErrors.length} error
              {serverErrors.length === 1 ? "" : "s"}. Nothing was created.
            </p>
            <div className="max-h-72 overflow-auto rounded border border-red-500/30">
              <table className="w-full text-xs">
                <thead className="bg-red-500/5">
                  <tr>
                    <th className="px-2 py-1 text-left">Row</th>
                    <th className="px-2 py-1 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {serverErrors.map((e, i) => (
                    <tr key={i} className="border-t border-red-500/20">
                      <td className="px-2 py-1 font-mono">{e.row || "—"}</td>
                      <td className="px-2 py-1">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-emerald-700">
              <CheckCircle2Icon className="mr-1 inline h-3.5 w-3.5" />
              Imported {success.imported_count} entit
              {success.imported_count === 1 ? "y" : "ies"} successfully.
            </p>
            <div className="flex flex-wrap gap-2">
              {success.entities.slice(0, 20).map((e) => (
                <Badge key={e.id} variant="online">
                  {e.code} · {e.name}
                </Badge>
              ))}
              {success.entities.length > 20 && (
                <Badge variant="offline">
                  …+{success.entities.length - 20} more
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
